import { supabase } from "../../supabase/supabase";

// An organization's call webhooks (Settings -> Webhooks), through the manage_webhook_endpoints
// function. The voice agent server posts each call event of the organization to its active
// webhooks, signed with the webhook's secret (#269).
// TODO(#270): deliver through Svix (retries, delivery history).

// Must match CLIENT_EVENT_TYPES in voxi-2-0 src/webhooks/clientEvent.js
export const WEBHOOK_EVENT_OPTIONS = [
  { id: "call.initiated", label: "Call Initiated" },
  { id: "call.ringing", label: "Call Ringing" },
  { id: "call.in-progress", label: "Call Answered" },
  { id: "call.voicemail-detected", label: "Voicemail Detected" },
  { id: "call.completed", label: "Call Completed" },
  { id: "call.failed", label: "Call Failed" },
];

export interface Webhook {
  id: string;
  url: string;
  description: string | null;
  events: string[]; // empty = all events
  is_active: boolean;
  created_at: string;
  updated_at: string;
  secret?: string; // owners and admins only
}

export interface WebhookInput {
  url: string;
  description?: string;
  events: string[];
  isActive: boolean;
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("manage_webhook_endpoints", { body });
  if (error) {
    // The function's own message (e.g. "url must start with https://")
    const message = await error.context?.json?.().then((b: any) => b?.error).catch(() => null);
    throw new Error(message || error.message);
  }
  return data as T;
}

export const webhookService = {
  /**
   * The organization's webhooks, and whether the caller can change them (owners and admins).
   */
  getWebhooks(organizationId: string) {
    return invoke<{ webhooks: Webhook[]; canManage: boolean }>({ organizationId, action: "list" });
  },

  async createWebhook(organizationId: string, input: WebhookInput): Promise<Webhook> {
    const { webhook } = await invoke<{ webhook: Webhook }>({ organizationId, action: "create", ...input });
    return webhook;
  },

  async updateWebhook(organizationId: string, endpointId: string, input: WebhookInput): Promise<Webhook> {
    const { webhook } = await invoke<{ webhook: Webhook }>({
      organizationId,
      action: "update",
      endpointId,
      ...input,
    });
    return webhook;
  },

  async deleteWebhook(organizationId: string, endpointId: string): Promise<void> {
    await invoke({ organizationId, action: "delete", endpointId });
  },
};
